// Helpers for `education_content` items. Videos live on Mux (muxPlaybackId) or,
// for content uploaded before the move, in Firebase Storage (downloadURL).
// Mux fields: muxStatus is 'uploading' | 'processing' | 'ready' | 'errored' | 'deleted'.

export const MUX_STATUS_LABELS = {
  uploading: 'Uploading',
  processing: 'Processing',
  ready: 'Ready',
  errored: 'Failed',
  deleted: 'Missing on Mux',
};

/** True when the item streams from Mux (encoded and playable). */
export const isMuxReady = (item) => Boolean(item?.muxPlaybackId) && item?.muxStatus === 'ready';

/** True when a video has anything the app can play right now. */
export const hasPlayableVideo = (item) => isMuxReady(item) || Boolean(item?.downloadURL);

/** Whether residents should see this item at all (hides trash and videos still encoding). */
export const isViewableContent = (item) =>
  Boolean(item) && !item.deleted && item.status !== 'retired' && (item.fileType !== 'video' || hasPlayableVideo(item));

/** Video uploaded to Storage before Mux, with no Mux asset yet. */
export const isLegacyVideo = (item) =>
  item?.fileType === 'video' && Boolean(item.downloadURL) && !item.muxAssetId && !item.muxPlaybackId;

/** Best URL for saving a copy: Mux's MP4 rendition when it exists, else the original file. */
export const getContentDownloadURL = (item) => {
  if (!item) return null;
  if (item.fileType === 'video' && item.muxDownloadURL) {
    const base = (item.originalFileName || item.fileName || item.title || 'video').replace(/\.[^/.]+$/, '');
    return `${item.muxDownloadURL}?download=${encodeURIComponent(`${base}.mp4`)}`;
  }
  return item.downloadURL || null;
};

/** Mux thumbnail for a playback id at a given time (seconds). */
export const muxThumbnailURL = (playbackId, time = 1, width = 640) =>
  `https://image.mux.com/${playbackId}/thumbnail.jpg?time=${Number(time) || 1}&width=${width}`;

const BUCKET = 'bcm-anesthesia-app.firebasestorage.app';

/**
 * Public URL for an object under education/ (storage rules allow reads there, so no token is
 * needed). The raw storage.googleapis.com form returns 403: objects are not IAM-public.
 */
export const educationFileURL = (storagePath) => `https://firebasestorage.googleapis.com/v0/b/${BUCKET}/o/${encodeURIComponent(storagePath)}?alt=media`;

/**
 * Best image for a card, or null for placeholder art:
 * an uploaded thumbnail, else Mux's frame at the admin's chosen time for a video,
 * else the first slide image the conversion function wrote for a presentation.
 */
export const thumbnailFor = (item, width = 640) => {
  if (!item) return null;
  if (item.thumbnailURL) return item.thumbnailURL;
  if (item.fileType === 'video' && item.muxPlaybackId) return muxThumbnailURL(item.muxPlaybackId, item.thumbnailTime ?? 1, width);
  if (item.fileType === 'presentation' && item.storagePath) return educationFileURL(`${item.storagePath.replace(/\.(ppt|pptx)$/i, '')}_slide_1.png`);
  return null;
};
